// src/hooks/useProtectedLinksHider.ts
import { useEffect } from "react";
import { fileProtectionService } from "../services/fileProtectionService";

// Простое base64 кодирование для скрытия URL
const encodeUrl = (url: string): string => {
  return btoa(url);
};

const decodeUrl = (encodedUrl: string): string => {
  try {
    return atob(encodedUrl);
  } catch {
    return "";
  }
};

export const useProtectedLinksHider = () => {
  useEffect(() => {
    const hideProtectedLinks = async () => {
      // Находим все ссылки на Azure blob storage
      const links = document.querySelectorAll(
        'a[href*="blob.core.windows.net"]',
      );

      for (const link of links) {
        const anchor = link as HTMLAnchorElement;
        const originalUrl = anchor.href;

        try {
          // Проверяем, требует ли файл аутентификации
          const { requiresAuth } =
            await fileProtectionService.checkFileAccess(originalUrl);

          if (requiresAuth) {
            // Кодируем URL в base64
            const encodedUrl = encodeUrl(originalUrl);

            // Заменяем href на закодированный URL с префиксом
            anchor.href = `#protected:${encodedUrl}`;

            // Добавляем атрибут для идентификации защищенных ссылок
            anchor.setAttribute("data-protected", "true");
            anchor.setAttribute("data-original-url", encodedUrl);

            // Добавляем визуальный индикатор (иконку замка)
            if (!anchor.querySelector(".protected-icon")) {
              const icon = document.createElement("span");
              icon.className = "protected-icon";
              //icon.innerHTML = ' 🔒';
              icon.style.fontSize = "0.8em";
              icon.style.marginLeft = "4px";
              anchor.appendChild(icon);
            }
          }
        } catch (error) {
          console.error("Error checking link protection:", originalUrl, error);
        }
      }
    };

    // Запускаем скрытие ссылок после небольшой задержки, чтобы DOM успел отрендериться
    const timeoutId = setTimeout(hideProtectedLinks, 100);

    // Также запускаем при изменениях в DOM (для динамически добавляемого контента)
    const observer = new MutationObserver((mutations) => {
      let hasNewLinks = false;
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (
                element.tagName === "A" &&
                element.getAttribute("href")?.includes("blob.core.windows.net")
              ) {
                hasNewLinks = true;
              } else if (
                element.querySelector &&
                element.querySelector('a[href*="blob.core.windows.net"]')
              ) {
                hasNewLinks = true;
              }
            }
          });
        }
      });

      if (hasNewLinks) {
        setTimeout(hideProtectedLinks, 100);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  // Функция для декодирования URL (может понадобиться в других частях приложения)
  const getOriginalUrl = (encodedUrl: string): string => {
    return decodeUrl(encodedUrl);
  };

  return { getOriginalUrl };
};
